import React, { useState, useEffect, useRef } from 'react';
import styles from './ProfileTab.module.css';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { updateDoc, doc } from 'firebase/firestore';
import { useFirestoreDoc } from '../../../hooks/useFirestoreDoc';
import { storage, db, firestore } from '../../../firebase/firebase';
import { useNotifications } from "../../../utils/NotificationContext/NotificationContext";
import MultiSelectTopics from './../../MultiSelectTopics/MultiSelectTopics';
import Button from '../../ui/Button';

import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import avatar from './../../../assets/default-avatar.svg';
import {
  HiUser, HiIdentification, HiTag, HiMapPin, HiUserGroup, HiAcademicCap,
  HiBuildingLibrary, HiAdjustmentsHorizontal, HiCurrencyDollar,
  HiCloudArrowUp, HiXCircle, HiMiniCheckCircle 
} from 'react-icons/hi2';

export default function ProfileTab({ user, readOnly, collapsed, compact }) {
  const { docData, loading, error, update } = useFirestoreDoc(`users/${user?.uid}`);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(false);
  const { t } = useTranslation();
  const { notify } = useNotifications();
  const toFixed = (n, fixed) => ~~(Math.pow(10, fixed) * n) / Math.pow(10, fixed);

  const countries = [
    "United States", "United Kingdom", "Canada", "Germany", "France",
    "Australia", "India", "Japan", "South Korea", "Brazil", "Nigeria"
  ];

  const affiliationTypes = [
    { label: t('profile.affiliationTypes.highschool'), value: 'highschool' },
    { label: t('profile.affiliationTypes.university'), value: 'university' },
    { label: t('profile.affiliationTypes.debateclub'), value: 'debateclub' },
    { label: t('profile.affiliationTypes.lawschool'), value: 'lawschool' },
    { label: t('profile.affiliationTypes.mediaorg'), value: 'mediaorg' },
    { label: t('profile.affiliationTypes.nonprofit'), value: 'nonprofit' },
    { label: t('profile.affiliationTypes.government'), value: 'government' },
    { label: t('profile.affiliationTypes.company'), value: 'company' }
  ];

  const experienceOptions = ['Novice', 'Intermediate', 'Expert'];
  const debateStyleOptions = ['Parliamentary', 'Oxford', 'Lincoln-Douglas', 'Public Forum'];

  useEffect(() => {
    if (!editing) return;
    const timer = setTimeout(async () => {
      await update(form);
      //notify('info', t('autosave.saved'));
    }, 800);
    return () => clearTimeout(timer);
  }, [form, editing]);


  const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const debouncedSave = useRef(debounce(async (fields) => {
    await update(fields);
    notify('info', t('autosave.saved'));
  }, 500)).current;

  useEffect(() => {
    if (docData) setForm(docData);
  }, [docData]);

  if (loading) return <div className={styles.message}>{t('profile.loading')}</div>;
  if (error) return <div className={styles.error}>{t('profile.error', { error: error.message })}</div>;
  if (!form) return null;

  // Compact view for sidebar mode
  if (collapsed) {
    return (
      <div className={styles.compact}>
        <img className={styles.compactAvatar} src={form.profilePicture || avatar} alt="Avatar" onError={e => e.target.src = avatar}/>
      </div>
    );
  }

  // Compact view for sidebar mode
  if (compact) {
    return (
      <div style={{flexDirection: "column"}} className={styles.compact}>
        <img
          className={styles.compactAvatar}
          src={form.profilePicture || avatar}
          alt="Avatar"
          onError={e => e.target.src = avatar} />
		  <div className={styles.field}>
			<span>{form.username || ''}</span>
		  </div>
      </div>
    );
  }

  
  /*const onChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (editing) debouncedSave({ [name]: value });
  };*/

  const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `avatars/${user?.uid}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    await update({ profilePicture: url });
    setForm(prev => ({ ...prev, profilePicture: url }));
    notify('success', t('avatar.uploadSuccess'));
  };

  const uploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file || file.size > 500000) {
      notify('error', t('profile.fileTooLarge'));
      return;
    }
    const storageRef = ref(storage, `affiliationLogos/${user?.uid}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    await update({ affiliationLogoUrl: url });
    setForm(prev => ({ ...prev, affiliationLogoUrl: url }));
    notify('success', t('avatar.uploadSuccess'));
  };



  /*const uploadAvatar = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const storageRef = ref(storage, `avatars/${user?.uid}`);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);
    await update({ profilePicture: url });
    notify('success', t('avatar.uploadSuccess'));
  };*/

  return (
	<div className={styles.wrapper}>
	<div className={styles.background}></div>
    <div className={styles.card}>
      <h3 className={styles.heading}>{t('profile.title')}</h3>
      <p className={styles.summary}>{t('profile.summary')}</p>

      <div className={styles.section}>
        <div className={styles.avatarSection}>
          <img className={styles.avatar} src={form.profilePicture || avatar} alt="Avatar" />
          {!readOnly && editing && (
            <label className={styles.uploadBtn}>
              <HiCloudArrowUp /> <span>{t('profile.upload')}</span>
              <input type="file" accept="image/*" hidden onChange={uploadAvatar} />
            </label>
          )}
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.field}><HiUser className={styles.icon} /><label>{t('profile.fullName')}</label></div>
        {editing ? <input name="fullName" value={form.fullName || ''} onChange={onChange} /> : <span>{form.fullName || t('profile.noName')}</span>}

        <div style={{marginTop: "2rem"}} className={styles.field}>
		<HiIdentification className={styles.icon} /><label>{t('profile.username')}</label></div>
        <span>{form.username || '-'}</span>
      </div>

      <div className={styles.section}>
        <div className={styles.field}><HiTag className={styles.icon} /><label>{t('profile.bio')}</label></div>
        {editing ? <textarea name="bio" value={form.bio || ''} onChange={onChange} /> : <p>{form.bio || t('profile.noBio')}</p>}
      </div>

      <div className={styles.gridSection}>
        {/* Location */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiMapPin className={styles.icon} /><label>{t('profile.location')}</label></div>
          {editing ? (
            <select name="location" value={form.location || ''} onChange={onChange}>
              <option value="">{t('profile.select')}</option>
              {countries.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <span>{form.location || '-'}</span>
          )}
        </div>

        {/* Pronouns */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiUserGroup className={styles.icon} /><label>{t('profile.pronouns')}</label></div>
          {editing ? <input name="pronouns" value={form.pronouns || ''} onChange={onChange} /> : <span>{form.pronouns || '-'}</span>}
        </div>

        {/* Experience */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiAcademicCap className={styles.icon} /><label>{t('profile.experience')}</label></div>
          {editing ? (
            <select name="experience" value={form.experience || ''} onChange={onChange}>
              <option value="">{t('profile.select')}</option>
              {experienceOptions.map(exp => <option key={exp} value={exp}>{exp}</option>)}
            </select>
          ) : (
            <span>{form.experience || '-'}</span>
          )}
        </div>

        {/* Debate Style */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiAdjustmentsHorizontal className={styles.icon} /><label>{t('profile.debateStyle')}</label></div>
          {editing ? (
            <select name="debateStyle" value={form.debateStyle || ''} onChange={onChange}>
              <option value="">{t('profile.select')}</option>
              {debateStyleOptions.map(style => <option key={style} value={style}>{style}</option>)}
            </select>
          ) : (
            <span>{form.debateStyle || '-'}</span>
          )}
        </div>

        {/* Topics */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiTag className={styles.icon} /><label>{t('profile.topics')}</label></div>
          {editing ? <MultiSelectTopics  value={form.topics || []} onChange={(topics) => setForm(prev => ({ ...prev, topics }))}  disabled={!editing} /> : <span>{form.topics.join(", ") || '-'}</span>}
        </div>

        {/* Affiliation */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiBuildingLibrary className={styles.icon} />
		  <label>{t('profile.affiliation')}</label></div>
          <div className={styles.affilate}>
		  {editing ? (
            <select name="affiliation" value={form.affiliation || ''} onChange={onChange}>
              <option value="">{t('profile.select')}</option>
              {affiliationTypes.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          ) : (
            <>{/*<span>{affiliationTypes.find(opt => opt.value === form.affiliation)?.label || '-'}</span>*/}</>
          )}
		  <div className={editing ? ''  : styles.affilatelogo}>
			  {!editing && form.affiliationLogoUrl && (
				<img src={form.affiliationLogoUrl} className={styles.logo} alt="Affiliation Logo" />
			  )}
			  
			  {editing ? (
				<>
				  <label>{t('profile.affiliationName')}</label>
				  <input className={styles.inbutons} name="affiliationName" value={form.affiliationName || ''} onChange={onChange} />
				  <label>{t('profile.affiliationLogo')}</label>
				  <input className={styles.inbutons} type="file" accept="image/*" onChange={uploadLogo} />
				</>
			  ) : (
				<>
					<>{form.affiliationName || '-'}</> (<>{affiliationTypes.find(opt => opt.value === form.affiliation)?.label || '-'}</>)
				</>
			  )}
		  </div>
		  </div>
        </div>

        {/* RatioCoin */}
        <div className={styles.subSection}>
          <div className={styles.field}><HiCurrencyDollar className={styles.icon} /><label>{t('profile.ratioCoin')}</label></div>
          <span>{toFixed(form.ratioCoin, 5) ?? 0} RTC</span>
        </div>
      </div>

      {!readOnly && (
        <div className={styles.actions}>
          {editing ? (
            <Button variant={'secondary'} className={styles.cancelBtn} onClick={() => setEditing(false)}>
              <HiMiniCheckCircle  /> {t('profile.done')}
            </Button>
          ) : (
            <Button variant={'secondary'} className={styles.editBtn} onClick={() => setEditing(true)}>
              <HiUser /> {t('profile.edit')}
            </Button>
          )}
        </div>
      )}
    </div>
    </div>
  );
 }
